import { syncState, when } from "@legendapp/state";
import { toast } from "sonner";

import { authClient } from "~/libs/auth.lib";
import { trpc } from "~/libs/trpc.lib";
import { user$ } from "../auth";
import { organizationInvitations$ } from "../organization-invitations/organization-invitations.crud";
import { activeOrganization$ } from "../organizations";

const INFO_INVITE_CODES = new Set([
  "USER_IS_ALREADY_INVITED_TO_THIS_ORGANIZATION",
  "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION",
]);

export const createInvitation = async (
  email: string,
  role: "member" | "admin",
) => {
  const trimmedEmail = email.trim();
  const { error } = await authClient.organization.inviteMember({
    email: trimmedEmail,
    role,
  });

  if (error) {
    if (error.code && INFO_INVITE_CODES.has(error.code)) {
      const message =
        error.code === "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION"
          ? `${trimmedEmail} is already a member`
          : `${trimmedEmail} has already been invited`;
      toast.info(message);
      return;
    }
    toast.error(error.message || "Failed to send invitation");
    throw new Error(error.message, { cause: error });
  }

  toast.success(`Invitation sent to ${trimmedEmail}`);
};

export const cancelInvitation = async (invitationId: string) => {
  const { error } = await authClient.organization.cancelInvitation({
    invitationId,
  });

  if (error) {
    toast.error(error.message || "Failed to cancel invitation");
    throw new Error(error.message, { cause: error });
  }

  toast.success("Invitation cancelled");
};

export const updateInvitationRole = async (
  invitationId: string,
  email: string,
  role: "member" | "admin",
) => {
  // Cancel the existing invitation
  const { error: cancelError } = await authClient.organization.cancelInvitation(
    {
      invitationId,
    },
  );

  if (cancelError) {
    toast.error(cancelError.message || "Failed to cancel existing invitation");
    throw new Error(cancelError.message, { cause: cancelError });
  }

  // Create a new invitation with the updated role
  const { error: inviteError } = await authClient.organization.inviteMember({
    email: email.trim(),
    role,
  });

  if (inviteError) {
    toast.error(inviteError.message || "Failed to send updated invitation");
    throw new Error(inviteError.message, { cause: inviteError });
  }

  toast.success("Invitation role updated successfully");
};

export const acceptInvitation = async (invitationId: string) => {
  const { error } = await authClient.organization.acceptInvitation({
    invitationId,
  });

  if (error) {
    toast.error(error.message || "Failed to accept invitation");
    throw new Error(error.message, { cause: error });
  }

  toast.success("Invitation accepted");

  // Wait for an active organization to be set
  await when(activeOrganization$.id);

  // Refresh the invitations (awaited so callers see settled state)
  await syncState(organizationInvitations$).sync();
};

/**
 * Accept an invitation during the onboarding wizard.
 *
 * Optimistically sets `user$.onboarding` to `"done"` before the server
 * round-trips begin so that no other observer (InvitationsStep, AppLayout)
 * can race and demote the user to a different step mid-flight. The server
 * `setUserStep` mutation persists the value; if anything fails, the
 * previous step is restored.
 *
 * Org-level onboarding state is intentionally left untouched -- the org
 * the user is joining may be at any step (typically `"done"` for a populated
 * workspace, but possibly mid-setup if the owner is also new). The layout
 * observer + route guards route the joiner accordingly.
 */
export const acceptInvitationDuringOnboarding = async (
  invitationId: string,
) => {
  const prevStep = user$.onboarding.get();
  user$.onboarding.set("done");

  try {
    await acceptInvitation(invitationId);
    await trpc.onboarding.setUserStep.mutate({ step: "done" });
    // Re-assert locally after the step is persisted. `acceptInvitation`
    // triggers a better-auth session refetch whose subscription writes the
    // server session back into `auth$`/`user$`, clobbering the optimistic
    // value above back to the pre-update step. Without this second set,
    // `user$.onboarding` lingers as the old step, the `/onboard` layout
    // observer never sees `"done"`, and the user is stranded on the now-empty
    // step until a reload pulls the fresh session.
    user$.onboarding.set("done");
  } catch (err) {
    user$.onboarding.set(prevStep);
    throw err;
  }
};

export const rejectInvitation = async (invitationId: string) => {
  const { error } = await authClient.organization.rejectInvitation({
    invitationId,
  });

  if (error) {
    toast.error(error.message || "Failed to reject invitation");
    throw new Error(error.message, { cause: error });
  }

  // Refresh the invitations
  await syncState(organizationInvitations$).sync();

  toast.success("Invitation rejected");
};
